import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import AdminDataTables from './admin/AdminDataTables';
import AlcoholControls, { CategoryControls } from './admin/AlcoholControls';
import RegionControls from './admin/RegionControls';
import { useFeatures } from '../features';
import {
  adminValidateCustomer,
  changePassword,
  createRegistrationCode,
  getRegistrationCodes,
  RegistrationCode,
  getLogArchiveStatus,
  getSchedulerStatus,
  isAdmin,
  isLoggedIn,
  LogArchiveStatus,
  runLogArchive,
  runScheduler,
  SchedulerStatus
} from '../api';

// Admin console: every partner, offer and claim in tables (partners can be added, edited and deleted;
// Manage opens a venue's partner dashboard in admin mode), issue partner
// sign-up codes, change the admin password, plus scheduler + log-archive controls ported from OfferScheduler.php / LogArch.php
// (README phase 3/4), plus the promo-code validation from c2n-php hotelAdmin.php.
const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const features = useFeatures();
  const [schedulerStatus, setSchedulerStatus] = useState<SchedulerStatus | null>(null);
  const [logStatus, setLogStatus] = useState<LogArchiveStatus | null>(null);
  const [forceArchive, setForceArchive] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [validateForm, setValidateForm] = useState({ ownerEmail: '', mobile: '', promoCode: '' });
  const [validateMessage, setValidateMessage] = useState<string | null>(null);

  const [codes, setCodes] = useState<RegistrationCode[]>([]);
  const [newCode, setNewCode] = useState<string | null>(null);
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '' });
  const [passwordMessage, setPasswordMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const errorText = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback;

  const handleCreateCode = async () => {
    setError(null);
    try {
      const code = await createRegistrationCode();
      setNewCode(code);
      setCodes(await getRegistrationCodes());
    } catch {
      setError('Failed to create registration code.');
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMessage(null);
    try {
      const result = await changePassword(passwordForm.current, passwordForm.next);
      setPasswordMessage({ ok: true, text: result.message });
      setPasswordForm({ current: '', next: '' });
    } catch (err) {
      setPasswordMessage({ ok: false, text: errorText(err, 'Failed to change password.') });
    }
  };

  const loadStatus = () => {
    getSchedulerStatus().then(setSchedulerStatus).catch(() => setError('Could not load scheduler status.'));
    getLogArchiveStatus().then(setLogStatus).catch(() => setError('Could not load log-archive status.'));
    getRegistrationCodes().then(setCodes).catch(() => setError('Could not load registration codes.'));
  };

  useEffect(() => {
    if (!isLoggedIn() || !isAdmin()) {
      navigate('/login?role=partner');
      return;
    }
    loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleRunScheduler = async () => {
    setMessage(null);
    setError(null);
    try {
      const result = await runScheduler();
      setMessage(`Scheduler ran: ${result.deactivated} deactivated, ${result.activated} activated, ${result.updated} offers updated.`);
      loadStatus();
    } catch {
      setError('Failed to run scheduler.');
    }
  };

  const handleRunLogArchive = async () => {
    setMessage(null);
    setError(null);
    try {
      const result = await runLogArchive(forceArchive);
      setMessage(result.message);
      loadStatus();
    } catch {
      setError('Failed to run log archive.');
    }
  };

  const handleValidateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidateMessage(null);
    try {
      const result = await adminValidateCustomer(validateForm.ownerEmail, validateForm.mobile, validateForm.promoCode);
      setValidateMessage(result.message);
    } catch {
      setValidateMessage('Failed to validate promo code.');
    }
  };

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto', mt: 6, px: { xs: 2, md: 3 }, pb: 8, position: 'relative', zIndex: 2 }}>
      <Typography variant="h4" fontFamily="Cormorant Garamond, serif" color="#c9a227" mb={3}>
        Admin Console
      </Typography>
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <AlcoholControls />
      <CategoryControls />
      <RegionControls />

      <AdminDataTables />

      <Divider sx={{ borderColor: '#333', mb: 4 }} />

      <Typography variant="h6" color="#e6e6e6" mb={1}>Partner Registration Codes</Typography>
      <Typography variant="body2" color="#8b8b95" mb={2}>
        A new partner needs one of these codes to register their venue. Each code works once.
      </Typography>
      {newCode && <Alert severity="success" sx={{ mb: 2 }}>New code: <b>{newCode}</b> (share it with the partner)</Alert>}
      <Button variant="contained" onClick={handleCreateCode} sx={{ background: 'linear-gradient(135deg, #c9a227, #dbb42c)', color: '#000', fontWeight: 700, mb: 2 }}>
        Generate Code
      </Button>
      <Typography variant="body2" color="#8b8b95" mb={4}>
        Unused codes: {codes.length === 0 ? 'none' : codes.map((c) => c.code).join(', ')}
      </Typography>

      <Divider sx={{ borderColor: '#333', mb: 4 }} />

      <Typography variant="h6" color="#e6e6e6" mb={1}>Offer Scheduler</Typography>
      <Typography variant="body2" color="#8b8b95" mb={2}>
        Last run: {schedulerStatus?.lastRunUtc ? new Date(schedulerStatus.lastRunUtc).toLocaleString() : 'never'}
      </Typography>
      <Button variant="contained" onClick={handleRunScheduler} sx={{ background: 'linear-gradient(135deg, #c9a227, #dbb42c)', color: '#000', fontWeight: 700, mb: 4 }}>
        Run Scheduler Now
      </Button>

      <Divider sx={{ borderColor: '#333', mb: 4 }} />

      <Typography variant="h6" color="#e6e6e6" mb={1}>Log Archive</Typography>
      <Typography variant="body2" color="#8b8b95" mb={2}>
        Source log: {logStatus?.sourceExists ? `${logStatus.sourceSizeBytes} bytes` : 'not found'}
        {logStatus?.exceedsThreshold ? ' (exceeds threshold)' : ''}
        <br />
        Backup: {logStatus?.backupExists ? `last updated ${logStatus.backupLastWriteUtc ? new Date(logStatus.backupLastWriteUtc).toLocaleString() : '-'}` : 'none yet'}
      </Typography>
      <FormControlLabel
        control={<Checkbox checked={forceArchive} onChange={(e) => setForceArchive(e.target.checked)} sx={{ color: '#8b8b95' }} />}
        label="Force archive even if below threshold"
        sx={{ color: '#8b8b95', mb: 1, display: 'block' }}
      />
      <Button variant="contained" onClick={handleRunLogArchive} sx={{ background: 'linear-gradient(135deg, #c9a227, #dbb42c)', color: '#000', fontWeight: 700, mb: 4 }}>
        Run Log Archive
      </Button>

      <Divider sx={{ borderColor: '#333', mb: 4 }} />

      {/* Promo-code redemption: only while offer claiming is switched on (it's off: discovery-only). */}
      {features.offerClaims && <>
      <Typography variant="h6" color="#e6e6e6" mb={1}>Verify Customer Promo Code</Typography>
      <Typography variant="body2" color="#8b8b95" mb={2}>
        Ported from hotelAdmin.php — validate a promo code on behalf of any owner account.
      </Typography>
      <Box component="form" onSubmit={handleValidateCustomer} display="flex" flexDirection="column" gap={2}>
        {validateMessage && <Alert severity="info">{validateMessage}</Alert>}
        <TextField label="Owner Email" required value={validateForm.ownerEmail} onChange={(e) => setValidateForm({ ...validateForm, ownerEmail: e.target.value })} sx={{ input: { color: '#e6e6e6' } }} InputLabelProps={{ style: { color: '#8b8b95' } }} />
        <TextField label="Customer Mobile" required value={validateForm.mobile} onChange={(e) => setValidateForm({ ...validateForm, mobile: e.target.value })} sx={{ input: { color: '#e6e6e6' } }} InputLabelProps={{ style: { color: '#8b8b95' } }} />
        <TextField label="Promo Code" required value={validateForm.promoCode} onChange={(e) => setValidateForm({ ...validateForm, promoCode: e.target.value })} sx={{ input: { color: '#e6e6e6' } }} InputLabelProps={{ style: { color: '#8b8b95' } }} />
        <Button type="submit" variant="contained" sx={{ background: 'linear-gradient(135deg, #c9a227, #dbb42c)', color: '#000', fontWeight: 700 }}>
          Verify
        </Button>
      </Box>

      <Divider sx={{ borderColor: '#333', my: 4 }} />
      </>}

      <Typography variant="h6" color="#e6e6e6" mb={2}>Change Admin Password</Typography>
      <Box component="form" onSubmit={handleChangePassword} display="flex" flexDirection="column" gap={2}>
        {passwordMessage && <Alert severity={passwordMessage.ok ? 'success' : 'error'}>{passwordMessage.text}</Alert>}
        <TextField type="password" label="Current Password" required value={passwordForm.current} onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })} sx={{ input: { color: '#e6e6e6' } }} InputLabelProps={{ style: { color: '#8b8b95' } }} />
        <TextField type="password" label="New Password (min 8 characters)" required value={passwordForm.next} onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })} sx={{ input: { color: '#e6e6e6' } }} InputLabelProps={{ style: { color: '#8b8b95' } }} />
        <Button type="submit" variant="contained" sx={{ background: 'linear-gradient(135deg, #c9a227, #dbb42c)', color: '#000', fontWeight: 700 }}>
          Change Password
        </Button>
      </Box>
    </Box>
  );
};

export default AdminPage;
